import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * Shared state for browsing a hosted GTFS network (PRD-04 FR13–FR16).
 *
 * The selected feed + selected route/stop drive **both** the in-map display
 * (`HostedNetworkDisplay`) and the Fixed Routes drawer's *Network (live)* section
 * (`NetworkRoutesSection`). Lives at App level (inside `FixedRoutesProvider`) so the
 * map subtree and the drawer subtree share one selection without prop-drilling.
 *
 * Backend-heavy: this holds only the *selection*, never the route list (the drawer
 * fetches a server-paged page) or geometry (loaded lazily per route on open).
 *
 * Network routes are intentionally **not** injected into `FixedRoutesContext.routes`
 * (which feeds simulation/export/stats). A live route only becomes a real `FixedRoute`
 * when the user forks it for editing (`source:'edited'`, see FR15/FR16).
 */

export interface NetworkDisplayStatus {
  total: number;
  shown: number;
  truncated: boolean;
}

interface NetworkBrowseContextValue {
  /** The hosted feed currently being browsed, or null. */
  feedId: string | null;
  setFeedId: (id: string | null) => void;
  /** Optional version pin (defaults to the feed's current version). */
  version: string | null;
  /** Active edit version (scenario) of the feed, or null for the Base feed (PRD-05c). */
  scenarioId: string | null;
  selectVersion: (scenarioId: string | null) => void;
  /** Route highlighted/previewed on the map + expanded in the drawer. */
  selectedRouteId: string | null;
  setSelectedRouteId: (id: string | null) => void;
  selectedStopId: string | null;
  setSelectedStopId: (id: string | null) => void;
  /** In-view overview counts surfaced by the map display (for the picker/section status line). */
  status: NetworkDisplayStatus | null;
  setStatus: (status: NetworkDisplayStatus | null) => void;
  /** Select a feed and reset the route/stop selection in one go. */
  selectFeed: (id: string | null) => void;
  /**
   * Whether the floating feed-picker window is open. Independent of the right-drawer
   * stack so it can float over (and coexist with) the Fixed Routes drawer, opened from
   * that drawer's header button and closed via its own control.
   */
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;
  /**
   * "Zoom to route" signal consumed by the in-map display (which holds the Leaflet map).
   * `nonce` increments on every request so repeat clicks on the same route re-fire.
   */
  zoomRequest: { routeId: string; nonce: number } | null;
  requestZoomToRoute: (routeId: string) => void;
  /**
   * A stop the user searched/picked: the in-map display flies to it (on `nonce` change)
   * and renders it as a highlighted pin even if it isn't an "important" stop.
   */
  focusedStop: { stopId: string; lat: number; lng: number; name?: string | null; nonce: number } | null;
  focusStop: (stop: { stopId: string; lat: number; lng: number; name?: string | null }) => void;
  /**
   * Chosen direction/shape variant for the selected route (PRD-04 FR6a). Scoped to a
   * `routeId` so a stale value is ignored when the selection moves to another route.
   */
  selectedVariant: { routeId: string; direction: number | null; shapeId: string | null } | null;
  setSelectedVariant: (v: { routeId: string; direction: number | null; shapeId: string | null } | null) => void;
}

const NetworkBrowseContext = createContext<NetworkBrowseContextValue | undefined>(undefined);

export function NetworkBrowseProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [feedId, setFeedId] = useState<string | null>(null);
  const [version] = useState<string | null>(null);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [status, setStatus] = useState<NetworkDisplayStatus | null>(null);
  const [zoomRequest, setZoomRequest] = useState<{ routeId: string; nonce: number } | null>(null);
  const [focusedStop, setFocusedStop] = useState<
    { stopId: string; lat: number; lng: number; name?: string | null; nonce: number } | null
  >(null);
  const [selectedVariant, setSelectedVariant] = useState<
    { routeId: string; direction: number | null; shapeId: string | null } | null
  >(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const zoomNonceRef = useRef(0);
  const stopNonceRef = useRef(0);

  const selectFeed = useCallback((id: string | null) => {
    setFeedId(id);
    setScenarioId(null); // switching network resets to its Base version
    setSelectedRouteId(null);
    setSelectedStopId(null);
    setStatus(null);
    setZoomRequest(null);
    setFocusedStop(null);
    setSelectedVariant(null);
  }, []);

  const selectVersion = useCallback((id: string | null) => {
    setScenarioId(id);
    setSelectedRouteId(null);
    setSelectedStopId(null);
    setSelectedVariant(null);
  }, []);

  // Zoom to a route: highlight it (so it stands out among the in-view overview) and
  // signal the in-map display to fit the map to its bounds.
  const requestZoomToRoute = useCallback((routeId: string) => {
    setSelectedRouteId(routeId);
    setSelectedStopId(null);
    zoomNonceRef.current += 1;
    setZoomRequest({ routeId, nonce: zoomNonceRef.current });
  }, []);

  // Focus a searched/picked stop: select it (highlight) and signal a fly-to.
  const focusStop = useCallback((stop: { stopId: string; lat: number; lng: number; name?: string | null }) => {
    setSelectedStopId(stop.stopId);
    stopNonceRef.current += 1;
    setFocusedStop({ ...stop, nonce: stopNonceRef.current });
  }, []);

  const value = useMemo<NetworkBrowseContextValue>(
    () => ({
      feedId,
      setFeedId,
      version,
      scenarioId,
      selectVersion,
      selectedRouteId,
      setSelectedRouteId,
      selectedStopId,
      setSelectedStopId,
      status,
      setStatus,
      selectFeed,
      zoomRequest,
      requestZoomToRoute,
      focusedStop,
      focusStop,
      selectedVariant,
      setSelectedVariant,
      pickerOpen,
      setPickerOpen,
    }),
    [
      feedId,
      version,
      scenarioId,
      selectVersion,
      selectedRouteId,
      selectedStopId,
      status,
      selectFeed,
      zoomRequest,
      requestZoomToRoute,
      focusedStop,
      focusStop,
      selectedVariant,
      pickerOpen,
    ],
  );

  return <NetworkBrowseContext.Provider value={value}>{children}</NetworkBrowseContext.Provider>;
}

export function useNetworkBrowse(): NetworkBrowseContextValue {
  const ctx = useContext(NetworkBrowseContext);
  if (!ctx) {
    throw new Error('useNetworkBrowse must be used within a NetworkBrowseProvider');
  }
  return ctx;
}
